import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    displayName: '',
    password: '',
    confirmPassword: '',
    realName: '',
    discordName: '',
    mahjongSoulName: '',
    clubAffiliation: 'Charleston' as 'Charleston' | 'Charlotte' | 'Washington D.C.'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }
    
    try {
      await register({
        email: formData.email,
        displayName: formData.displayName,
        password: formData.password,
        realName: formData.realName.trim() || undefined,
        discordName: formData.discordName.trim() || undefined,
        mahjongSoulName: formData.mahjongSoulName.trim() || undefined,
        clubAffiliation: formData.clubAffiliation
      });
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/login"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field mt-1"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                Display Name
              </label>
              <input
                id="displayName"
                name="displayName"
                type="text"
                autoComplete="display-name"
                required
                minLength={2}
                maxLength={30}
                className="input-field mt-1"
                placeholder="Choose a display name"
                value={formData.displayName}
                onChange={handleChange}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="input-field pr-10"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  className="input-field pr-10"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="clubAffiliation" className="block text-sm font-medium text-gray-700">
                Club Affiliation
              </label>
              <select
                id="clubAffiliation"
                name="clubAffiliation"
                required
                className="input-field mt-1"
                value={formData.clubAffiliation}
                onChange={handleChange}
              >
                <option value="Charleston">Charleston</option>
                <option value="Charlotte">Charlotte</option>
                <option value="Washington D.C.">Washington D.C.</option>
              </select>
            </div>

            <div>
              <label htmlFor="realName" className="block text-sm font-medium text-gray-700">
                Real Name <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input
                id="realName"
                name="realName"
                type="text"
                className="input-field mt-1"
                placeholder="Your real name"
                value={formData.realName}
                onChange={handleChange}
                maxLength={30}
              />
            </div>

            <div>
              <label htmlFor="discordName" className="block text-sm font-medium text-gray-700">
                Discord Name <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input
                id="discordName"
                name="discordName"
                type="text"
                className="input-field mt-1"
                placeholder="Your Discord username"
                value={formData.discordName}
                onChange={handleChange}
                maxLength={30}
              />
            </div>

            <div>
              <label htmlFor="mahjongSoulName" className="block text-sm font-medium text-gray-700">
                Mahjong Soul Name <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input
                id="mahjongSoulName"
                name="mahjongSoulName"
                type="text"
                className="input-field mt-1"
                placeholder="Your Mahjong Soul username"
                value={formData.mahjongSoulName}
                onChange={handleChange}
                maxLength={30}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;

